import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Fragment, useDeferredValue, useEffect, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon, type IconName } from '@/components/Icon'
import { StateView } from '@/card/record'
import type { AuditChange, AuditEntry, AuditPage as Page } from '@/card/HistoryPanel'
import { Pager } from '@/lists/Pager'
import { request } from '@/lib/api'
import { AdminPage, AdminState, SkeletonTable, initials, isForbidden } from './AdminShell'
import { entityTypeLabel, fieldLabel } from './labels'

/**
 * Журнал аудита для администратора (тикет #104, макет AdmAudit.dc.html): все изменения, с
 * фильтрами по пользователю, типу записи, действию и периоду.
 *
 * Право — `system.audit`, отдельное от `system.settings`: читать, кто что менял, и
 * настраивать систему — разные обязанности. Журнал только читается — правки здесь нет и
 * быть не может, поэтому и баннера «только просмотр» нет.
 *
 * Название записи (`entityLabel`) и названия значений-ссылок (`beforeLabel`/`afterLabel`)
 * сервер отдаёт только по записям, которые смотрящий вправе читать: `system.audit` открывает
 * факт изменения, но не саму сделку. Без названия — тип и короткий идентификатор, целиком в
 * подсказке, чтобы по нему можно было найти запись.
 *
 * Чего журнал не отдаёт, то экран не придумывает: адреса и источника входа, событий входа и
 * просмотра ПДн.
 */

interface Filter {
  actor: string
  entityType: string
  action: string
  from: string
  to: string
}

const EMPTY: Filter = { actor: '', entityType: '', action: '', from: '', to: '' }
const ACTIONS = ['INSERT', 'UPDATE', 'DELETE', 'PERMISSIONS', 'TAGS']

const ACTION_BADGE: Record<string, { cls: string; icon: IconName }> = {
  INSERT: { cls: 'badge b-success', icon: 'plus' },
  UPDATE: { cls: 'badge b-info', icon: 'adm-edit' },
  DELETE: { cls: 'badge b-danger', icon: 'trash' },
  PERMISSIONS: { cls: 'badge b-warning', icon: 'lock' },
  TAGS: { cls: 'badge', icon: 'flag' },
}

export const fetchAudit = (filter: Filter, cursor: string | null, signal?: AbortSignal) => {
  const params = new URLSearchParams()
  if (filter.actor.trim()) params.set('actor', filter.actor.trim())
  if (filter.entityType) params.set('entityType', filter.entityType)
  if (filter.action) params.set('action', filter.action)
  // Dates are whole days in the viewer's zone: "to" includes the chosen day.
  if (filter.from) params.set('from', new Date(`${filter.from}T00:00:00`).toISOString())
  if (filter.to) {
    const end = new Date(`${filter.to}T00:00:00`)
    end.setDate(end.getDate() + 1)
    params.set('to', end.toISOString())
  }
  if (cursor) params.set('cursor', cursor)
  const query = params.toString()
  return request<Page>(`/admin/audit${query ? `?${query}` : ''}`, { signal })
}

export const fetchAuditEntityTypes = (signal?: AbortSignal) =>
  request<{ entityTypes: string[] }>('/admin/audit/entity-types', { signal }).then((r) => r.entityTypes)

export function AuditPage() {
  const { t, locale } = useApp()
  const [filter, setFilter] = useState<Filter>(EMPTY)
  // The login is typed letter by letter; the journal is big, and a request per letter
  // would load it for nothing.
  const actor = useDeferredValue(filter.actor)
  const applied = { ...filter, actor }
  const types = useQuery({ queryKey: ['audit-entity-types'], queryFn: ({ signal }) => fetchAuditEntityTypes(signal) })
  const journal = useInfiniteQuery({
    queryKey: ['admin-audit', applied],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => fetchAudit(applied, pageParam, signal),
    getNextPageParam: (last) => last.nextCursor ?? null,
  })
  const pages = journal.data?.pages ?? []
  const [page, setPage] = useState(0)
  const [open, setOpen] = useState<Set<number>>(new Set())
  // Another filter is another journal: its first page, not the n-th page of the previous one.
  const filterKey = JSON.stringify(applied)
  useEffect(() => setPage(0), [filterKey])

  const at = Math.min(page, Math.max(0, pages.length - 1))
  const entries = pages[at]?.entries ?? []
  const before = pages.slice(0, at).reduce((sum, item) => sum + item.entries.length, 0)
  const filtered = JSON.stringify(filter) !== JSON.stringify(EMPTY)
  const forbidden = isForbidden(journal.error)

  const toggle = (id: number) => {
    const next = new Set(open)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setOpen(next)
  }
  const when = (iso: string) => {
    const date = new Date(iso)
    const tag = locale === 'en' ? 'en-GB' : 'ru-RU'
    return {
      day: date.toLocaleDateString(tag),
      time: date.toLocaleTimeString(tag, { hour: '2-digit', minute: '2-digit' }),
    }
  }

  return (
    <AdminPage titleKey="nav.adminAudit" leadKey="adm.audit.lead" forbidden={forbidden}>
      {!forbidden && (
        <form className="toolbar" aria-label={t('audit.filters')} onSubmit={(event) => event.preventDefault()}>
          <span className="row" style={{ gap: 6 }}>
            <Icon name="adm-calendar" className="muted" />
            <input type="date" className="input input-sm" style={{ width: 140 }} aria-label={t('audit.from')}
                   value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} />
            <span className="muted">—</span>
            <input type="date" className="input input-sm" style={{ width: 140 }} aria-label={t('audit.to')}
                   value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} />
          </span>
          <div className="input-icon" style={{ width: 210 }}>
            <Icon name="user" />
            <input className="input input-sm" aria-label={t('audit.actor')} placeholder={t('adm.audit.actorPh')}
                   value={filter.actor} onChange={(e) => setFilter({ ...filter, actor: e.target.value })} />
          </div>
          <select className="input input-sm" style={{ width: 190 }} aria-label={t('audit.entityType')} value={filter.entityType}
                  onChange={(e) => setFilter({ ...filter, entityType: e.target.value })}>
            <option value="">{t('adm.audit.typeAll')}</option>
            {(types.data ?? []).map((type) => (
              <option key={type} value={type}>{entityTypeLabel(t, type)}</option>
            ))}
          </select>
          <select className="input input-sm" style={{ width: 190 }} aria-label={t('audit.action')} value={filter.action}
                  onChange={(e) => setFilter({ ...filter, action: e.target.value })}>
            <option value="">{t('adm.audit.actionAll')}</option>
            {ACTIONS.map((action) => (
              <option key={action} value={action}>{t(`adm.audit.action.${action}`)}</option>
            ))}
          </select>
          {filtered && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFilter(EMPTY)}>{t('audit.reset')}</button>
          )}
        </form>
      )}
      <AdminState
        isLoading={journal.isLoading}
        error={journal.error}
        onRetry={() => void journal.refetch()}
        forbiddenTitleKey="adm.audit.forbidden"
        errorTitleKey="adm.audit.error"
        skeleton={<SkeletonTable columns={5} rows={14} />}
      >
        {entries.length === 0 ? (
          <StateView
            boxed
            kind="empty"
            title={filtered ? t('audit.empty') : t('adm.audit.none')}
            description={filtered ? t('adm.audit.empty.text') : undefined}
            actions={filtered && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFilter(EMPTY)}>
                {t('adm.resetFilters')}
              </button>
            )}
          />
        ) : (
          <>
            <div className="tbl-wrap">
              <table className="tbl" aria-label={t('history.table')}>
                <thead>
                  <tr>
                    <th className="adm-chev"><span className="sr-only">{t('adm.audit.details')}</span></th>
                    <th>{t('history.when')}</th>
                    <th>{t('history.who')}</th>
                    <th>{t('history.action')}</th>
                    <th>{t('adm.audit.what')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    const expanded = open.has(entry.id)
                    const moment = when(entry.at)
                    const badge = ACTION_BADGE[entry.action] ?? { cls: 'badge', icon: 'file' as IconName }
                    const type = entityTypeLabel(t, entry.entityType)
                    return (
                      <Fragment key={entry.id}>
                        <tr data-testid={`audit-${entry.id}`} className={expanded ? 'is-open' : undefined}>
                          <td className="adm-chev">
                            {entry.changes.length > 0 && (
                              <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-expanded={expanded}
                                      aria-label={t(expanded ? 'adm.audit.hide' : 'adm.audit.show', { what: type })}
                                      onClick={() => toggle(entry.id)}>
                                <Icon name="chevron" size="sm" className={expanded ? undefined : 'adm-rot'} />
                              </button>
                            )}
                          </td>
                          <td><span className="secondary">{moment.day}</span> {moment.time}</td>
                          <td>
                            {entry.actorName ? (
                              <span className="row" style={{ gap: 8 }}><span className="ava">{initials(entry.actorName)}</span>{entry.actorName}</span>
                            ) : (
                              <span className="muted">{t('history.system')}</span>
                            )}
                          </td>
                          <td>
                            <span className={badge.cls}><Icon name={badge.icon} />{t(`adm.audit.action.${entry.action}`)}</span>
                          </td>
                          <td>
                            {entry.entityLabel && <span className="strong">{entry.entityLabel} · </span>}
                            <span className="muted">{type}</span>
                            {!entry.entityLabel && entry.entityId && (
                              <span className="mono t-sm muted" title={entry.entityId}> · {entry.entityId.slice(0, 8)}</span>
                            )}
                            {!expanded && entry.changes.length > 0 && (
                              <span className="t-sm muted"> · {t('adm.audit.fields', { count: entry.changes.length })}</span>
                            )}
                          </td>
                        </tr>
                        {expanded && (
                          <tr className="detail">
                            <td colSpan={5}>
                              <div className="stack" style={{ gap: 6 }}>
                                {entry.changes.map((change) => (
                                  <Diff key={change.field} entry={entry} change={change} />
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Pager
              from={before + 1}
              to={before + entries.length}
              hasPrev={at > 0}
              hasNext={at < pages.length - 1 || Boolean(journal.hasNextPage)}
              busy={journal.isFetchingNextPage}
              pageSize={entries.length}
              onPrev={() => setPage(at - 1)}
              onNext={() => {
                if (at < pages.length - 1) setPage(at + 1)
                else void journal.fetchNextPage().then(() => setPage(at + 1))
              }}
            />
          </>
        )}
      </AdminState>
    </AdminPage>
  )
}

/**
 * «Было → стало». Создание показывает только «стало», удаление — только «было»; пустое
 * значение названо словом, а не пустой ячейкой — иначе «очистили поле» не отличить от
 * «поле не менялось». Поле без права — «скрыто», а не пустота: это правда о правах, а не
 * о данных.
 */
function Diff({ entry, change }: { entry: AuditEntry; change: AuditChange & { label?: string | null } }) {
  const { t } = useApp()
  const label = fieldLabel(t, change.field, change.label)
  if (change.hidden) {
    return (
      <div className="diff">
        <span className="f">{label}</span>
        <span className="old nil">{t('history.hidden')}</span>
        <span className="arr" aria-hidden="true">→</span>
        <span className="new nil">{t('history.hidden')}</span>
      </div>
    )
  }
  const before = entry.action === 'INSERT' ? null : change.before
  const after = entry.action === 'DELETE' ? null : change.after
  return (
    <div className="diff">
      <span className="f">{label}</span>
      <Value value={before} name={entry.action === 'INSERT' ? null : change.beforeLabel} side="old" />
      <span className="arr" aria-hidden="true">→</span>
      <Value value={after} name={entry.action === 'DELETE' ? null : change.afterLabel} side="new" />
    </div>
  )
}

function Value({ value, name, side }: { value: unknown; name?: string | null; side: 'old' | 'new' }) {
  const { t } = useApp()
  if (value == null || value === undefined || value === '') {
    return <span className={`${side} nil`}>{t('adm.audit.nil')}</span>
  }
  const text = name ?? (typeof value === 'object' ? JSON.stringify(value) : String(value))
  return (
    <span className={side} title={name ? String(value) : undefined}>
      <span className="sr-only">{t(side === 'old' ? 'adm.audit.was' : 'adm.audit.became')} </span>
      {text}
    </span>
  )
}
